import {
    AdminRoleId,
    AgGridColumnDef,
    AgGridFilterType,
    FilterOption,
    SortOption
} from '@tassat/common-definitions';
import { UserRole } from '@tassat/fred-definitions';

import { exportApiFirmData, exportManageFirmData } from 'Lib/exports/firmExports';
import { caseInsensitiveSort } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { FirmInfoCellRenderer } from 'SharedComponents/cellRenderComponent/firmInfoCellRenderer';
import { IFeaturesContext } from 'SharedComponents/hooks/useFeatures';

import { GridFirmData } from '.';

export type RowData = GridFirmData;

const didFirmUpdate = (firmA: GridFirmData, firmB: GridFirmData) => {
    if (!firmA) {
        return !firmB;
    } else {
        return firmA.FirmId === firmB.FirmId &&
            firmA.Name === firmB.Name &&
            firmA.IsTokenAPIFirm === firmB.IsTokenAPIFirm &&
            firmA.IsIBNEnabled === firmB.IsIBNEnabled &&
            firmA.IsLocked === firmB.IsLocked &&
            firmA.IsFrozen === firmB.IsFrozen &&
            firmA.IsClosed === firmB.IsClosed &&
            firmA.IsDeleted === firmB.IsDeleted &&
            firmA.HasApiConnections === firmB.HasApiConnections;
    }
};

export const getColumnDefs = (
    cellHeight: number,
    muiTheme: ApplicationMuiTheme
): AgGridColumnDef[] => {
    const defaultDefs: AgGridColumnDef[] = [
        {
            field: 'FirmInfo',
            headerName: 'Firm Info Loading',
            cellRenderer: FirmInfoCellRenderer,
            cellRendererParams: { cellHeight: cellHeight - 2 },
            valueGetter: (params) => params.data,
            equals: didFirmUpdate,
            flex: 1
        },
        {
            field: 'Name',
            colId: 'Name',
            filter: 'agTextColumnFilter',
            comparator: caseInsensitiveSort,
            hide: true,
            sort: 'asc'
        },
        {
            field: 'IsActive',
            colId: 'IsActive',
            filter: 'agTextColumnFilter',
            hide: true
        },
        {
            field: 'IsTokenAPIFirm',
            colId: 'IsTokenAPIFirm',
            filter: 'agTextColumnFilter',
            comparator: (valueA) => (valueA ? -1 : 1),
            hide: true
        },
        {
            field: 'IsLocked',
            colId: 'IsLocked',
            filter: 'agTextColumnFilter',
            hide: true
        },
        {
            field: 'IsFrozen',
            colId: 'IsFrozen',
            filter: 'agTextColumnFilter',
            hide: true
        },
        {
            field: 'IsClosed',
            colId: 'IsClosed',
            filter: 'agTextColumnFilter',
            hide: true
        }
    ];

    return defaultDefs;
};

export const getSortOptions = (role: UserRole, muiTheme: ApplicationMuiTheme): SortOption[] => {
    const defaultFirmSortOptions: SortOption[] = [
        {
            title: 'Firm Name',
            colId: 'Name'
        },
        {
            title: 'Firm Activity',
            colId: 'Activity'
        },
        {
            title: 'TAPI Enabled',
            colId: 'IsTokenAPIFirm'
        },
        {
            title: 'Is Locked',
            colId: 'IsLocked'
        },
        {
            title: 'Is Frozen',
            colId: 'IsFrozen'
        },
        {
            title: 'Clear Sort',
            colId: 'clear'
        }
    ];

    const noStatsFirmSortOptions: SortOption[] = [
        {
            title: 'Firm Name',
            colId: 'Name'
        },
        {
            title: 'TAPI Enabled',
            colId: 'IsTokenAPIFirm'
        },
        {
            title: 'Is Locked',
            colId: 'IsLocked'
        },
        {
            title: 'Is Frozen',
            colId: 'IsFrozen'
        },
        {
            title: 'Clear Sort',
            colId: 'clear'
        }
    ];

    switch (role.AdminRoleId) {
        case AdminRoleId.API_OPS:
            return noStatsFirmSortOptions;
        default:
            return defaultFirmSortOptions;
    }
};

export const getFilterOptions = (role: UserRole, muiTheme: ApplicationMuiTheme): FilterOption[] => {
    const defaultFirmFilterOptions: FilterOption[] = [
        {
            title: 'TAPI Firms',
            colId: 'IsTokenAPIFirm',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Locked Firms',
            colId: 'IsLocked',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Frozen Firms',
            colId: 'IsFrozen',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Closed Firms',
            colId: 'IsClosed',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Clear Filter',
            colId: 'clear',
            type: AgGridFilterType.ClearFilter,
            filter: 'clear'
        }
    ];

    const apiFirmFilterOptions: FilterOption[] = [
        {
            title: 'TAPI Firms',
            colId: 'IsTokenAPIFirm',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Locked Firms',
            colId: 'IsLocked',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Frozen Firms',
            colId: 'IsFrozen',
            type: AgGridFilterType.Equal,
            filter: 'true'
        },
        {
            title: 'Clear Filter',
            colId: 'clear',
            type: AgGridFilterType.ClearFilter,
            filter: 'clear'
        }
    ];

    switch (role.AdminRoleId) {
        case AdminRoleId.API_OPS:
            return apiFirmFilterOptions;
        default:
            return defaultFirmFilterOptions;
    }
};

export const getExportFunction = (
    role: UserRole,
    applicationName: string,
    features?: IFeaturesContext
): (filter: FilterOption, applicationName?: string, features?: IFeaturesContext) => void => {
    switch (role.AdminRoleId) {
        case AdminRoleId.API_OPS:
            return (filter) => exportApiFirmData(filter, applicationName);
        case AdminRoleId.BANK_OPS:
        case AdminRoleId.CLIENT_SERVICES:
        case AdminRoleId.BANK_OPS_SUPERVISOR:
            return (filter) => exportManageFirmData(filter, features);
        // case AdminRoleId.PCG_ADMIN:
        //     return exportPcgFirmData;
        default:
            return;
    }
};